import React from "react";
import {
  ShoppingCart,
  Heart,
  Share2,
  Copy,
  Check,
  X,
  Truck,
  Mail,
  Loader2,
} from "lucide-react";
import { WhatsappIcon } from "react-share";
import type { ProductInfoProps } from "./types";
import { useAppSetting } from "../../hooks/useAppSetting";
// import SocialProofIndicators from "./SocialProofIndicators";

const ProductInfo: React.FC<ProductInfoProps> = ({
  product,
  hasDiscount,
  originalPrice,
  discountedPrice,
  quantity,
  onQuantityChange,
  onAddToCart,
  cartError,
  cartLoading = false,
  cartSuccess = false,
  onWishlistClick,
  isWishlisted,
  wishlistLoading,
  user,
  selectedOptions,
  selectedSku,
  onOptionSelect,
}) => {
  const [showShareMenu, setShowShareMenu] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  const { value: deliveryDays } = useAppSetting<number>("delivery_days");

  const currentUrl = window.location.href;
  const shareText = `Check out ${product.name} - ${currentUrl}`;

  const shareToWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    window.open(url, "_blank");
    setShowShareMenu(false);
  };

  const shareToFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      currentUrl,
    )}`;
    window.open(url, "_blank");
    setShowShareMenu(false);
  };

  const shareToTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      `Check out ${product.name}!`,
    )}&url=${encodeURIComponent(currentUrl)}`;
    window.open(url, "_blank");
    setShowShareMenu(false);
  };

  const shareToPinterest = () => {
    const imageUrl =
      product.image_paths && product.image_paths[0]
        ? product.image_paths[0]
        : "";
    const url = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(
      currentUrl,
    )}&media=${encodeURIComponent(imageUrl)}&description=${encodeURIComponent(
      product.name || "",
    )}`;
    window.open(url, "_blank");
    setShowShareMenu(false);
  };

  const shareViaEmail = () => {
    const subject = `Check out ${product.name}`;
    const body = `I found this product and thought you might like it:\n\n${product.name}\n${currentUrl}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setShowShareMenu(false);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = currentUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const orderByWhatsapp = () => {
    const phone = "+96181389098"; // Replace with your store's WhatsApp number
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(shareText)}`;
    window.open(url, "_blank");
  };

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest(".share-menu-container")) {
        setShowShareMenu(false);
      }
    };

    if (showShareMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showShareMenu]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-section-text mb-2">{product.name}</h1>

        {product.description && (
          <p className="text-section-muted leading-relaxed mb-4">
            {product.description}
          </p>
        )}

        {/* Social Proof Indicators */}
        {/* <SocialProofIndicators productId={product.id} /> */}
      </div>

      {/* Variant selectors */}
      {product.has_variants && product.variant_tree && (
        <div className="space-y-4">
          {product.variant_tree.types.map((type) => (
            <div key={type.id}>
              <p className="text-sm font-medium text-section-muted mb-2">
                {type.name}
                {selectedOptions?.[type.name] && (
                  <span className="ml-2 text-section-text font-semibold">
                    {selectedOptions[type.name]}
                  </span>
                )}
              </p>
              <div className="flex flex-wrap gap-2">
                {type.options.map((opt) => {
                  const isSelected = selectedOptions?.[type.name] === opt.value;
                  return (
                    <button
                      key={opt.id}
                      onClick={() => onOptionSelect?.(type.name, opt.value)}
                      className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all duration-150 ${
                        isSelected
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-section-border bg-section-surface text-section-text hover:border-section-text"
                      }`}
                    >
                      {opt.value}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pricing */}
      <div className="border-t border-section-border pt-6">
        <div className="flex items-baseline gap-3 mb-2">
          {product.has_variants && !selectedSku ? (
            // No SKU selected yet — show price range
            <span className="text-4xl font-bold text-section-text">
              {product.min_variant_price === product.max_variant_price
                ? `$${(product.min_variant_price ?? 0).toFixed(2)}`
                : `$${(product.min_variant_price ?? 0).toFixed(2)} – $${(product.max_variant_price ?? 0).toFixed(2)}`}
            </span>
          ) : hasDiscount ? (
            <>
              <span className="text-4xl font-bold text-section-text">
                ${discountedPrice.toFixed(2)}
              </span>
              <span className="text-xl text-section-muted line-through">
                ${originalPrice.toFixed(2)}
              </span>
              <span className="bg-accent/15 text-accent px-3 py-1 rounded-full text-sm font-semibold border border-accent/30">
                {selectedSku?.sale_rate ?? product.sale_rate}% OFF
              </span>
            </>
          ) : (
            <span className="text-4xl font-bold text-section-text">
              ${originalPrice.toFixed(2)}
            </span>
          )}
        </div>

        {hasDiscount && (
          <p className="text-sm text-accent font-medium mb-4">
            You save ${(originalPrice - discountedPrice).toFixed(2)}
          </p>
        )}

        {/* Delivery Estimate */}
        {typeof deliveryDays === "number" && deliveryDays > 0 && (
          <div className="mt-4 p-3 bg-section-surface border border-section-border rounded-lg">
            <div className="flex items-start gap-2">
              <Truck className="shrink-0 w-5 h-5 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-section-text">
                  Estimated Delivery
                </p>
                <p className="text-sm text-section-muted">
                  {new Date(
                    Date.now() + deliveryDays * 24 * 60 * 60 * 1000,
                  ).toLocaleDateString("en-US", {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                  })}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Stock Status */}
        <div className="flex items-center gap-2 mt-4">
          {(() => {
            if (product.has_variants) {
              if (!selectedSku) return null;
              const variantQty = selectedSku.quantity ?? 0;
              if (
                !selectedSku.is_unlimited_stock &&
                (selectedSku.stock_status === "out_of_stock" || variantQty === 0)
              ) {
                return (
                  <span className="font-medium text-section-muted" role="status" aria-live="polite">
                    Out of stock
                  </span>
                );
              }
              if (!selectedSku.is_unlimited_stock && variantQty < 5) {
                return (
                  <span className="font-medium text-accent" role="status" aria-live="polite">
                    Low in stock
                  </span>
                );
              }
              return <span className="font-medium text-section-text">In Stock</span>;
            }
            const qty = typeof product.quantity === "number" ? product.quantity : 0;
            if (!product.is_unlimited_stock && qty === 0) {
              return (
                <span className="font-medium text-section-muted" role="status" aria-live="polite">
                  Out of stock
                </span>
              );
            }
            if (!product.is_unlimited_stock && qty < 5) {
              return (
                <span className="font-medium text-accent" role="status" aria-live="polite">
                  Low in stock
                </span>
              );
            }
            return <span className="font-medium text-section-text">In Stock</span>;
          })()}
        </div>
      </div>

      {/* Quantity and Actions */}
      <div className="border-t border-section-border pt-6">
        {(() => {
          const effectiveStock = product.has_variants
            ? (selectedSku?.is_unlimited_stock ? 99 : selectedSku?.quantity ?? 0)
            : (product.is_unlimited_stock ? 99 : product.quantity ?? 0);
          const variantOutOfStock =
            !!selectedSku &&
            !selectedSku.is_unlimited_stock &&
            (selectedSku.stock_status === "out_of_stock" ||
              (selectedSku.quantity ?? 0) === 0);
          const isOutOfStock = product.has_variants
            ? variantOutOfStock
            : !product.is_unlimited_stock && effectiveStock === 0;
          const needsVariantSelection = product.has_variants && !selectedSku;
          const addToCartDisabled =
            isOutOfStock || needsVariantSelection;

          return (
            <>
        <div className="flex items-center gap-4 mb-6">
          <span className="text-sm font-medium text-section-muted">
            Quantity:
          </span>
          <div className="flex items-center border border-section-border bg-section-surface rounded-lg overflow-hidden">
            <button
              onClick={() => onQuantityChange("decrement")}
              className="p-3 hover:bg-section-border transition-colors text-section-text"
              disabled={quantity <= 1 || isOutOfStock}
            >
              <span className="text-lg font-semibold">−</span>
            </button>
            <input
              type="number"
              value={quantity > effectiveStock ? effectiveStock : quantity}
              readOnly
              className="w-16 text-center border-0 bg-transparent text-section-text focus:ring-0 p-0"
            />
            <button
              onClick={() => onQuantityChange("increment")}
              className="p-3 hover:bg-section-border transition-colors text-section-text"
              disabled={quantity >= effectiveStock || isOutOfStock}
            >
              <span className="text-lg font-semibold">+</span>
            </button>
          </div>
        </div>

        {/* Cart error message */}
        {cartError && (
          <p className="text-red-500 text-sm mb-3">{cartError}</p>
        )}

        {/* Action Buttons */}
        <div className="grid grid-cols-[1fr_auto_auto] items-stretch gap-2 sm:flex sm:flex-row sm:gap-4">
          <button
            className="flex-1 bg-primary text-primary-foreground py-4 px-6 rounded-xl hover:opacity-90 transition-all font-bold text-lg flex items-center justify-center gap-2 shadow-[0_4px_14px_rgba(201,169,110,0.2)] col-span-1 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={addToCartDisabled || cartLoading}
            onClick={onAddToCart}
          >
            {cartLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Adding...
              </>
            ) : cartSuccess ? (
              <>
                <Check className="w-5 h-5" />
                Added!
              </>
            ) : isOutOfStock ? (
              <>
                <ShoppingCart className="w-5 h-5" />
                Out of Stock
              </>
            ) : needsVariantSelection ? (
              <>
                <ShoppingCart className="w-5 h-5" />
                Select a variant
              </>
            ) : (
              <>
                <ShoppingCart className="w-5 h-5" />
                Add to Cart
              </>
            )}
          </button>

          <div className="flex gap-2 sm:ml-0 justify-self-end">
            <button
              className={`h-14 w-14 flex items-center justify-center border rounded-xl transition-all duration-200 ${
                isWishlisted
                  ? "bg-red-500/10 border-red-500/50 hover:bg-red-500/20"
                  : "bg-section-surface border-section-border hover:border-section-text hover:bg-section-border"
              }`}
              onClick={onWishlistClick}
              disabled={wishlistLoading}
              title={
                !user
                  ? "Login to use wishlist"
                  : isWishlisted
                    ? "Remove from wishlist"
                    : "Add to wishlist"
              }
            >
              <Heart
                className={`w-6 h-6 transition-colors ${
                  isWishlisted ? "fill-red-500 text-red-500" : "text-section-text"
                }`}
              />
            </button>
            <div className="relative share-menu-container">
              <button
                className="h-14 w-14 flex items-center justify-center border border-section-border bg-section-surface rounded-xl hover:bg-section-border transition-all duration-200 hover:border-section-text active:scale-95"
                onClick={() => setShowShareMenu((prev) => !prev)}
                type="button"
                aria-label="Share"
              >
                <Share2 className="w-6 h-6 text-section-text" />
              </button>

              {showShareMenu && (
                <>
                  {/* Mobile overlay */}
                  <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden" />

                  {/* Share menu */}
                  <div className="fixed bottom-0 left-0 right-0 bg-section-card-bg rounded-t-3xl shadow-2xl z-50 md:absolute md:z-10 md:right-0 md:bottom-auto md:top-full md:left-auto md:mt-2 md:w-72 md:rounded-xl md:shadow-xl md:border md:border-section-border animate-in slide-in-from-bottom-4 md:slide-in-from-top-2 duration-200">
                    {/* Mobile header */}
                    <div className="flex items-center justify-between p-6 border-b border-section-border md:hidden">
                      <h3 className="text-xl font-semibold text-section-text">
                        Share Product
                      </h3>
                      <button
                        onClick={() => setShowShareMenu(false)}
                        className="p-2 hover:bg-section-surface rounded-full transition-colors"
                      >
                        <X className="w-6 h-6 text-section-muted" />
                      </button>
                    </div>

                    {/* Share options */}
                    <div className="p-4 md:p-3 space-y-2">
                      {/* WhatsApp */}
                      <button
                        onClick={shareToWhatsApp}
                        className="w-full flex items-center gap-4 p-4 md:p-3 hover:bg-section-surface rounded-xl transition-all duration-200 group"
                      >
                        <div
                          style={{ backgroundColor: "#25d366" }}
                          className="w-10 h-10 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-200"
                        >
                          <WhatsappIcon size={20} className="text-white" />
                        </div>
                        <div className="flex-1 text-left">
                          <div className="font-semibold text-section-text text-sm">
                            WhatsApp
                          </div>
                        </div>
                      </button>

                      {/* Facebook */}
                      <button
                        onClick={shareToFacebook}
                        className="w-full flex items-center gap-4 p-4 md:p-3 hover:bg-section-surface rounded-xl transition-all duration-200 group"
                      >
                        <div
                          style={{ backgroundColor: "#0965fe" }}
                          className="w-10 h-10 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-200"
                        >
                          <span className="text-white font-bold text-lg">
                            f
                          </span>
                        </div>
                        <div className="flex-1 text-left">
                          <div className="font-semibold text-section-text text-sm">
                            Facebook
                          </div>
                        </div>
                      </button>

                      {/* Twitter */}
                      <button
                        onClick={shareToTwitter}
                        className="w-full flex items-center gap-4 p-4 md:p-3 hover:bg-section-surface rounded-xl transition-all duration-200 group"
                      >
                        <div
                          style={{ backgroundColor: "#1DA1F2" }}
                          className="w-10 h-10 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-200"
                        >
                          <span className="text-white font-bold text-sm">
                            X
                          </span>
                        </div>
                        <div className="flex-1 text-left">
                          <div className="font-semibold text-section-text text-sm">
                            Twitter
                          </div>
                        </div>
                      </button>

                      {/* Pinterest */}
                      <button
                        onClick={shareToPinterest}
                        className="w-full flex items-center gap-4 p-4 md:p-3 hover:bg-section-surface rounded-xl transition-all duration-200 group"
                      >
                        <div
                          style={{ backgroundColor: "#E60023" }}
                          className="w-10 h-10 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-200"
                        >
                          <span className="text-white font-bold text-lg">
                            P
                          </span>
                        </div>
                        <div className="flex-1 text-left">
                          <div className="font-semibold text-section-text text-sm">
                            Pinterest
                          </div>
                        </div>
                      </button>

                      {/* Email */}
                      <button
                        onClick={shareViaEmail}
                        className="w-full flex items-center gap-4 p-4 md:p-3 hover:bg-section-surface rounded-xl transition-all duration-200 group"
                      >
                        <div className="w-10 h-10 bg-section-surface border border-section-border rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                          <Mail className="w-5 h-5 text-section-muted" />
                        </div>
                        <div className="flex-1 text-left">
                          <div className="font-semibold text-section-text text-sm">
                            Email
                          </div>
                        </div>
                      </button>

                      {/* Copy Link */}
                      <button
                        onClick={copyToClipboard}
                        className="w-full flex items-center gap-4 p-4 md:p-3 hover:bg-section-surface rounded-xl transition-all duration-200 group"
                      >
                        <div className="w-10 h-10 bg-section-surface border border-section-border rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                          {copied ? (
                            <Check className="w-5 h-5 text-green-500" />
                          ) : (
                            <Copy className="w-5 h-5 text-section-muted" />
                          )}
                        </div>
                        <div className="flex-1 text-left">
                          <div className="font-semibold text-section-text text-sm">
                            {copied ? "Copied!" : "Copy Link"}
                          </div>
                        </div>
                      </button>
                    </div>

                    {/* Mobile cancel button */}
                    <div className="p-4 border-t border-section-border md:hidden">
                      <button
                        onClick={() => setShowShareMenu(false)}
                        className="w-full py-3 text-center text-section-muted font-medium"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* WhatsApp Order Button */}
        <button
          className="w-full mt-4 bg-green-600 text-white py-3 px-6 rounded-xl hover:bg-green-700 transition-colors font-medium shadow-lg shadow-green-900/20"
          onClick={orderByWhatsapp}
        >
          Order on WhatsApp
        </button>
            </>
          );
        })()}
      </div>
    </div>
  );
};

export default ProductInfo;
